import React from 'react';
import { Footer } from '../components/footer';
import { Navigation } from '../components/navigation';
import { Page } from '../pages/pages-types';
import clsx from 'clsx';
import { pageStore } from '../pages/page-store';
import { useTranslation } from 'react-i18next';
import { Link } from '@remix-run/react';

export const ContentPage: React.FC<Page> = ({
  heroImage,
  heroImageAlt,
  title: titleKey,
  readingTime,
  summary,
  content,
  linkedPages,
  maxWidth = false,
}) => {
  const { t } = useTranslation(['home', 'common']);

  const allLinkedPages = linkedPages?.(pageStore) ?? [];
  const title = t(titleKey) as string;
  const imageAlt = t(heroImageAlt) as string;

  return (
    <div className="w-full bg-gradient-to-b from-green-50 via-white to-emerald-50/30 min-h-screen">
      {/* Navigation with always visible background on content pages */}
      <Navigation shouldShowNav={true} />

      {/* Hero Section with Text Overlay */}
      <section className="w-full relative h-[400px] md:h-[500px]">
        <img className="object-cover w-full h-full" alt={imageAlt} src={heroImage} />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />

        {/* Hero Content - Left aligned */}
        <div className="absolute inset-0 flex flex-col justify-end pb-10 md:pb-14">
          <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600/90 text-white text-xs font-semibold uppercase tracking-wider rounded-full mb-4">
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {readingTime} {t('common:min-read')}
            </span>
            <h1 className="text-white font-bold text-3xl md:text-4xl lg:text-5xl font-display leading-tight max-w-3xl">
              {title}
            </h1>
            {summary && (
              <div className="text-white/85 text-base md:text-lg leading-relaxed mt-4 max-w-2xl">
                {summary as React.ReactNode}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="py-12 md:py-16">
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          {/* Content Area */}
          <article
            className={clsx(
              'prose prose-lg prose-emerald',
              'prose-headings:font-display prose-headings:text-emerald-900 prose-headings:font-bold',
              'prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-5',
              'prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4',
              'prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-5',
              'prose-strong:text-emerald-800 prose-strong:font-semibold',
              'prose-a:text-emerald-600 prose-a:font-medium prose-a:underline prose-a:underline-offset-2',
              'prose-ul:my-5 prose-li:text-gray-700 prose-li:marker:text-emerald-500',
              maxWidth ? 'max-w-none' : 'max-w-3xl',
            )}
          >
            {content}
          </article>
        </div>

        {/* Linked Pages Section */}
        {allLinkedPages && allLinkedPages.length > 0 && (
          <section className="mt-16 md:mt-20 pt-12 border-t border-emerald-100">
            <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-8 h-0.5 bg-emerald-500" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-emerald-700">
                  Continue Reading
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl">
                {allLinkedPages.map(
                  (
                    {
                      path,
                      readingTime: linkedReadingTime,
                      subtitle,
                      title: linkedTitle,
                      heroImage: linkedHeroImage,
                    },
                    index,
                  ) => (
                    <Link key={index} prefetch="intent" to={path} className="group">
                      <article className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 h-full flex flex-col border border-gray-100 hover:border-emerald-200">
                        {linkedHeroImage && (
                          <div className="h-36 overflow-hidden">
                            <img
                              src={linkedHeroImage}
                              alt=""
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        )}
                        <div className="p-5 flex flex-col flex-1">
                          <h3 className="font-display text-emerald-800 group-hover:text-emerald-600 text-lg font-bold transition-colors">
                            {t(linkedTitle)}
                          </h3>
                          {subtitle && (
                            <p className="text-gray-600 text-sm mt-2 line-clamp-2 flex-1">
                              {t(subtitle)}
                            </p>
                          )}
                          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                            <span className="text-xs text-gray-500 uppercase tracking-wider">
                              {linkedReadingTime} {t('common:min-read')}
                            </span>
                            <span className="text-emerald-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                              Read &rarr;
                            </span>
                          </div>
                        </div>
                      </article>
                    </Link>
                  ),
                )}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};
